import { ReactElement, forwardRef, useRef, useState } from "react";

import { useExpandCollapseAnimation } from "chameleon/lib/hooks/use-expand-collapse-animation";

import { RadioButton } from "../radio-button/radio-button";
import { Text } from "../text/text";
import {
  borderClass,
  childrenClass,
  collapsedClass,
  containerClass,
  containerFocusedClass,
  disabledBorderClass,
  disabledSelectedBorderClass,
  messageWrapperClass,
  radioButtonMessageWrapper,
  selectedBorderClass,
} from "./radio-tile.css";

export interface RadioTileProps {
  /**
   * Optionally add children React components to be rendered inside the box, below the radio button
   */
  children?: JSX.Element | null;
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * option type
   */
  type?: string;
  /**
   * Is the Button disabled?
   */
  isDisabled?: boolean;
  /**
   * Is button selected
   */
  isSelected: boolean;
  /**
   * onChange handler
   */
  onChange: (s: string, type: string) => void;
  /**
   * onKeyDown handler
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Title component
   */
  title?: ReactElement;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Value of radio button
   */
  value: string;
  /**
   * Boolean flag to show/hide children
   */
  showChildren?: boolean;
  /**
   * Resetting animation whenever the string changes
   */
  resetAnimation?: string;
  /**
   * Array of caption messages
   */
  messages?: string[];
  /**
   * radio group name
   */
  name?: string;
}

export const RadioTile = forwardRef<HTMLInputElement, RadioTileProps>(
  (
    {
      id,
      type,
      isDisabled,
      isSelected,
      onChange,
      onKeyDown,
      tabIndex,
      title,
      value,
      children,
      showChildren,
      resetAnimation,
      messages,
      name,
    }: RadioTileProps,
    ref
  ) => {
    const childrenRef = useRef<HTMLDivElement | null>(null);
    useExpandCollapseAnimation({
      componentRef: childrenRef,
      expand: showChildren,
      resetAnimation,
    });

    const [isRadioFocused, setIsRadioFocused] = useState(false);
    const focussedClassLogic: string = isRadioFocused
      ? containerFocusedClass
      : "";
    const showChildrenLogic: string = showChildren ? "" : collapsedClass;
    // color and state management logic
    let borderClassSelectedLogic = "";
    if (isSelected) {
      borderClassSelectedLogic = selectedBorderClass;
      if (isDisabled) {
        borderClassSelectedLogic = disabledSelectedBorderClass;
      }
    } else if (isDisabled) {
      borderClassSelectedLogic = disabledBorderClass;
    }

    const handleOnFocus = () => {
      setIsRadioFocused(true);
    };

    const handleOnBlur = () => {
      setIsRadioFocused(false);
    };

    const onClickSection = () => {
      onChange(value, type ?? "");
    };

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <div
        className={`${containerClass} ${borderClass} ${borderClassSelectedLogic} ${focussedClassLogic}`}
        onClick={onClickSection}
        data-testid={`${id}-radio-button`}
      >
        <div className={`${radioButtonMessageWrapper}`}>
          <RadioButton
            id={id}
            ref={ref}
            title={title}
            value={value}
            tabIndex={tabIndex}
            isSelected={isSelected}
            isDisabled={isDisabled}
            onKeyDown={onKeyDown}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            name={name}
          />
          {messages?.length ? (
            <div
              className={`${messageWrapperClass}`}
              data-testid={`${id}-messages`}
            >
              {messages.map((message) => (
                <Text key={message} styleVariant="body" tagElement="span">
                  {message}
                </Text>
              ))}
            </div>
          ) : null}
        </div>
        {children ? (
          <div
            id={`${id}-children`}
            ref={childrenRef}
            className={`${isSelected ? childrenClass : ""} ${showChildrenLogic}`}
          >
            {children}
          </div>
        ) : null}
      </div>
    );
  }
);

RadioTile.displayName = "RadioTile";
